import { useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { ColumnProps } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import { Tooltip, Tooltip as TooltipType } from 'primereact/tooltip';
import { Chip, Table, Spinner, Typography } from '@uni-of-exeter/digital.lib.react.ui.kit';

import './styles/resource-table.css';
import { APP_TEXT } from '../const/app-text';
import ContinueModal from './modals/continue-modal';
import ResourceTypeModal from './modals/resource-type';
import LikesInfoModal from './modals/likes-info-modal';
import { SortButton } from './resource-table/sort-button';
import ProgressInfoModal from './modals/progress-info-modal';
import ResourceLocationModal from './modals/resource-location';
import EvaluateResourceModal from './modals/evaluate-resource';
import { useResourceTable } from '../hooks/custom/use-resource-table';
import { locationCellTemplate, isProgressStatus } from '../utils/resource-table-helpers';
import { ProgressStatus } from '../const/progress';
import HideRecommendationModal from './modals/hide-recommendation-modal';
import { useBookmarks } from '../hooks/custom/bookmarking/use-bookmarks';
import { BookmarkColumnBody } from './resource-table/bookmark-resource-column';
import { RemoveBookmarkColumnBody } from './resource-table/remove-bookmark-column';
import { ResourceTableColumnHeader } from './resource-table/resource-table-column-header';
import { ColumnHeaderInfoButton } from './resource-table/column-header-information-button';
import { HideRecommendationColumnBody } from './resource-table/hide-recommendation-column';
import { ResourceTableProps, EnhancedResourceTableData } from './shared/resource-table-types';

const text = APP_TEXT.components.resourceTable;
const externalResourceModal = APP_TEXT.components.externalResourceModal;

const sortOrderMap: Record<number, 'none' | 'ascending' | 'descending'> = {
  0: 'none',
  1: 'ascending',
  2: 'descending',
};

const progressStatusMap: Record<ProgressStatus, string> = {
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'Started',
  COMPLETED: 'Completed',
};

export const ResourceTable = ({
  resources,
  isLoading = false,
  isError = false,
  getCategoryColor,
  source,
  rowsToShow = 10,
  setCurrentPage,
  totalItems,
  currentPage,
  handleSort,
  sortField = undefined,
  sortOrder = 0,
  defaultPagination = false,
  enableRatingSystem = false,
  enableHideRecommendations = false,
  enableSkillColumnSorting = false,
  enableProgressColumnSorting = false,
  enableBookmarkDeleteColumn = false,
  enableResourceBookmarking = false,
}: ResourceTableProps) => {
  const {
    // State
    progress,
    selectedExternalResource,
    evaluateResourceModalOpen,
    resourceToEvaluate,
    tableData,
    progressOptions,
    recommendationToHide,

    // Modal states
    resourceTypeModal,
    resourceLocationModal,
    progressModal,
    likesModal,
    hideRecommendationModal,

    // Event handlers
    handleLikeClick,
    handleProgressChange,
    handleViewClick,
    handleExternalContinue,
    getProgressDropdownClass,
    confirmHideRecommendation,

    // Setters
    setSelectedExternalResource,
    setEvaluateResourceModalOpen,
    setResourceToEvaluate,
    setRecommendationToHide,
  } = useResourceTable({
    resources,
    source,
    enableRatingSystem,
    getCategoryColor,
  });

  const { handleCreateBookmark, handleDeleteBookmark } = useBookmarks();

  const tooltipRef = useRef<TooltipType | null>(null);

  const enhancedTableData: EnhancedResourceTableData[] = tableData.map((data) => ({
    ...data,
    titleString: data.title,
    title: (
      <a
        rel="noreferrer"
        className="resourceTitleUrl resourceTitleTarget"
        href={data.url}
        target="_blank"
        aria-label={data.title}
        tabIndex={0}
        onClick={(e) => {
          e.preventDefault();
          handleViewClick(data);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleViewClick(data);
          }
        }}
      >
        {source == 'RecommendationPage' && data.isRoleDeptMatched === true && (
          <img
            src={text.roleDepartmentMatch.icon}
            alt={text.roleDepartmentMatch.alt}
          />
        )}
        {data.title}
      </a>
    ),
    category: (
      <Chip
        className="category"
        label={data.category}
        backgroundColor={getCategoryColor(data.category)}
        role="status"
        style={{ border: `1px solid ${getCategoryColor(data.category)}` }}
        aria-label={`Category: ${data.category}`}
      />
    ),
  }));

  const columns: ColumnProps[] = [
    {
      field: 'title',
      header: (
        <ResourceTableColumnHeader
          text={{
            headerText: text.headers.title,
          }}
        />
      ),
      style: {
        width: '294px',
      },
    },
    {
      field: 'category',
      header: (
        <ResourceTableColumnHeader
          text={{
            headerText: text.headers.digitalSkillsCategory,
          }}
        />
      ),
      bodyClassName: 'category-body',
      pt: {
        bodyCell: {
          tabIndex: 0,
        },
      },
      style: {
        width: '204px',
      },
    },
    {
      field: 'mediaType',
      header: (
        <th
          scope="col"
          className="resourceTableHeader"
          tabIndex={0}
          aria-sort="none"
        >
          <ColumnHeaderInfoButton
            text={{
              ariaLabel: text.accessibility.resourceTypeIIcon,
            }}
            onClick={resourceTypeModal.onTrue}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                resourceTypeModal.onTrue();
              }
            }}
          />
          <Typography
            variant="body"
            className="resourceTableHeaderText"
          >
            {text.headers.resourceType}
          </Typography>
        </th>
      ),
      pt: {
        bodyCell: {
          tabIndex: 0,
        },
      },
      style: {
        width: '128px',
      },
    },
    {
      header: (
        <th
          scope="col"
          tabIndex={0}
          className="resourceTableHeader"
          aria-sort="none"
        >
          <ColumnHeaderInfoButton
            text={{
              ariaLabel: text.accessibility.resourceLocationIIcon,
            }}
            onClick={resourceLocationModal.onTrue}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                resourceLocationModal.onTrue();
              }
            }}
          />
          <Typography
            variant="body"
            className="resourceTableHeaderText"
          >
            {text.headers.resourceLocation}
          </Typography>
        </th>
      ),
      pt: {
        bodyCell: {
          tabIndex: 0,
        },
      },
      body: (data: EnhancedResourceTableData) => (
        <Typography
          className="resourceTableColumnBodyText"
          variant="body"
        >
          {locationCellTemplate(data.isInternal, data.personalLogin)}
        </Typography>
      ),
      style: {
        width: '128px',
      },
    },
    {
      field: 'skillLevel',
      header: (
        <th
          scope="col"
          className="resourceTableHeader"
          tabIndex={0}
          aria-sort={sortField === 'skillLevel' ? sortOrderMap[sortOrder] : 'none'}
        >
          <Typography
            variant="body.xs"
            className="resourceTableHeaderText"
          >
            {text.headers.skillLevel}
          </Typography>
          {enableSkillColumnSorting && (
            <div className="sortButtonContainer">
              {sortField === 'skillLevel' && (
                <SortButton
                  sortOrder={sortOrderMap[sortOrder]}
                  ariaLabel={text.accessibility.sortSkillLevelBtn}
                  onClick={() => handleSort?.('skillLevel')}
                />
              )}
              {sortField != 'skillLevel' && (
                <SortButton
                  sortOrder={'none'}
                  ariaLabel={text.accessibility.sortSkillLevelBtn}
                  onClick={() => handleSort?.('skillLevel')}
                />
              )}
            </div>
          )}
        </th>
      ),
      pt: {
        bodyCell: {
          tabIndex: 0,
        },
      },
      style: {
        width: '128px',
      },
    },
    {
      header: (
        <th
          scope="col"
          className="resourceTableHeader"
          tabIndex={0}
          aria-sort={sortField === 'userProgressStatus' ? sortOrderMap[sortOrder] : 'none'}
        >
          <ColumnHeaderInfoButton
            text={{
              ariaLabel: text.accessibility.progressIcon,
            }}
            onClick={progressModal.onTrue}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                progressModal.onTrue();
              }
            }}
          />
          <Typography
            variant="body"
            className="resourceTableHeaderText"
          >
            {text.headers.progress}
          </Typography>
          {enableProgressColumnSorting && (
            <div className="sortButtonContainer">
              {sortField === 'userProgressStatus' && (
                <SortButton
                  sortOrder={sortOrderMap[sortOrder]}
                  ariaLabel={text.accessibility.sortUserProgressStatusBtn}
                  onClick={() => handleSort?.('userProgressStatus')}
                />
              )}
              {sortField != 'userProgressStatus' && (
                <SortButton
                  sortOrder={'none'}
                  ariaLabel={text.accessibility.sortUserProgressStatusBtn}
                  onClick={() => handleSort?.('userProgressStatus')}
                />
              )}
            </div>
          )}
        </th>
      ),
      body: (data: EnhancedResourceTableData) => (
        <Dropdown
          value={progress[data.id] || ProgressStatus.NotStarted}
          inputId={`Progress-select-input-${data.id}`}
          options={progressOptions}
          onChange={(e) => {
            const value = e.value;
            if (isProgressStatus(value)) {
              handleProgressChange(data.id, value);
            }
          }}
          placeholder="Select progress option"
          className={`${getProgressDropdownClass(progress[data.id] || ProgressStatus.NotStarted)}`}
          tabIndex={0}
          name="progressDropdownSelect"
          itemTemplate={(option) => (
            <div
              className="progressDropdownItem"
              key={option.value}
            >
              <Typography
                variant="body.xs"
                className="progressDropdownText"
              >
                {option.label}
              </Typography>
              <div className="dropdownSpacer" />
            </div>
          )}
          pt={{
            input: {
              'aria-label': `${text.accessibility.progressInput} ${progressStatusMap[progress[data.id]]}`,
              role: 'textbox',
            },
            select: {
              'aria-label': text.accessibility.progressSelect,
              label: text.accessibility.progressDropdownLabel,
            },
            root: {
              'aria-activedescendant': undefined,
            },
          }}
        />
      ),
      style: {
        width: '128px',
      },
    },
  ];

  if (enableHideRecommendations) {
    columns.push({
      header: (
        <ResourceTableColumnHeader
          text={{
            headerText: text.headers.hideRecommendation,
          }}
        />
      ),
      body: (data: EnhancedResourceTableData) => (
        <HideRecommendationColumnBody
          onClick={(e) => {
            e.preventDefault();
            setRecommendationToHide(data.id);
            hideRecommendationModal.onTrue();
          }}
          onKeyDown={(e) => {
            e.preventDefault();
            if (e.key === 'Enter' || e.key === ' ') {
              setRecommendationToHide(data.id);
              hideRecommendationModal.onTrue();
            }
          }}
          text={{
            btnAriaLabel: text.accessibility.hideRecommendationIcon,
            tooltipText: text.tooltips.hideRecommendation,
          }}
        />
      ),
      style: {
        width: '54px',
      },
    });
  }

  if (enableRatingSystem) {
    columns.push({
      header: (
        <th
          scope="col"
          className="resourceTableHeader"
          tabIndex={0}
          aria-sort="none"
        >
          <ColumnHeaderInfoButton
            text={{
              ariaLabel: text.accessibility.likesIcon,
            }}
            onClick={likesModal.onTrue}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                likesModal.onTrue();
              }
            }}
          />
          <Typography
            variant="body"
            className="resourceTableHeaderText"
          >
            {text.headers.likes}
          </Typography>
        </th>
      ),
      body: (data: EnhancedResourceTableData) => (
        <div
          id="ratingBtnWrapper"
          className="columnBtnWrapper"
        >
          <span
            className="ratingBox thumbIcon"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            role="button"
            onClick={() => handleLikeClick(data.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleLikeClick(data.id);
              }
            }}
          >
            <>
              <Typography
                variant="body.small"
                className="ratingsNumber"
                aria-label={text.accessibility.likesButton}
              >
                {data.likeCount}
              </Typography>
              <i className={`fa-${data.liked ? 'solid' : 'regular'} fa-thumbs-up thumbIcon`} />
            </>
          </span>
        </div>
      ),
      style: {
        width: '54px',
      },
    });
  }

  if (enableResourceBookmarking) {
    columns.push({
      header: (
        <ResourceTableColumnHeader
          text={{
            headerText: text.headers.bookmarkResource,
          }}
        />
      ),
      body: (data: EnhancedResourceTableData) => (
        <BookmarkColumnBody
          onClick={(event) => {
            event.preventDefault();

            if (!data.isBookmarked) {
              tooltipRef.current?.hide();

              handleCreateBookmark(data.id, data.titleString, data.isBookmarked);
            }
          }}
          text={{
            btnToolTipText: `${data.isBookmarked ? '' : text.tooltips.createBookmark}`,
            btnClassName: `bookmarkResourceBtn ${data.isBookmarked ? 'isActive' : ''}`,
            btnAriaLabel: data.isBookmarked
              ? text.accessibility.bookmarkedResource
              : text.accessibility.bookmarkResource,
          }}
        />
      ),
      style: {
        width: '54px',
      },
    });
  }

  if (enableBookmarkDeleteColumn) {
    columns.push({
      header: (
        <ResourceTableColumnHeader
          text={{
            headerText: text.headers.removeResourceBookmark,
          }}
        />
      ),
      body: (data: EnhancedResourceTableData) => (
        <RemoveBookmarkColumnBody
          onClick={(event) => {
            event.preventDefault();
            handleDeleteBookmark(data.id, data.titleString, data.isBookmarked);
          }}
          text={{
            btnAriaLabel: text.accessibility.removeBookmarkedResource,
            btnClassName: 'removeBookmarkBtn',
            btnTooltipText: text.tooltips.removeBookmark,
          }}
        />
      ),
      style: {
        width: '54px',
      },
    });
  }

  if (isLoading) {
    return (
      <div className="flexCenter">
        <Spinner aria-label={text.accessibility.loadingResourceSpinner} />
      </div>
    );
  }

  if (isError) {
    return <div>{text.error}</div>;
  }

  return (
    <div
      className="resourceTable"
      id="resourceTable"
    >
      <Table
        columns={columns}
        data={enhancedTableData}
        rowsPerPage={rowsToShow}
        pagination={defaultPagination}
      />
      {!defaultPagination && resources.length > 0 && (
        <Paginator
          className="resourceTableMobilePaginator"
          first={currentPage === undefined ? 0 : currentPage * rowsToShow}
          rows={rowsToShow}
          totalRecords={totalItems}
          onPageChange={(e) => setCurrentPage && setCurrentPage(e.page)}
        />
      )}
      <ResourceTypeModal
        isOpen={resourceTypeModal.value}
        onClose={resourceTypeModal.onFalse}
      />
      <ResourceLocationModal
        isOpen={resourceLocationModal.value}
        onClose={resourceLocationModal.onFalse}
      />
      <ProgressInfoModal
        isOpen={progressModal.value}
        onClose={progressModal.onFalse}
      />
      <LikesInfoModal
        isOpen={likesModal.value}
        onClose={likesModal.onFalse}
      />
      <HideRecommendationModal
        isOpen={hideRecommendationModal.value}
        onClose={hideRecommendationModal.onFalse}
        onConfirm={() => {
          confirmHideRecommendation(recommendationToHide);
        }}
      />
      <Tooltip
        ref={tooltipRef}
        target=".hasTooltip"
        appendTo={() => document.body}
        className="xxSmallTooltip"
      />
      <ContinueModal
        isOpen={selectedExternalResource !== null}
        onContinue={handleExternalContinue}
        onClose={() => setSelectedExternalResource(null)}
        title={externalResourceModal.title}
        body={externalResourceModal.body}
        primaryButtonText={externalResourceModal.continue}
        secondaryButtonText={externalResourceModal.back}
      />
      <EvaluateResourceModal
        isOpen={evaluateResourceModalOpen}
        onContinue={() => {
          if (resourceToEvaluate !== null) {
            handleLikeClick(resourceToEvaluate);
            setEvaluateResourceModalOpen(false);
            setResourceToEvaluate(null);
          }
        }}
        onClose={() => setEvaluateResourceModalOpen(false)}
      />
    </div>
  );
};
